import React, { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Typography, Row, Col, Button, Switch, InputNumber, Steps, message, Card, Radio, Slider } from "antd";

import { bundles } from "../../data/data";
import pricingData from '../../data/pricingData.json';
import "../Plans/Plans.scss";
import "./PlanActivationAndPayment.scss"
import "../../../../../../styles/helper.scss"
import axios from "axios";
import ENVIRONMENT from '../../../../../../environments'
import { CheckOutlined, LoadingOutlined, BankOutlined, CreditCardOutlined } from "@ant-design/icons";
import SaveCardFlow from "../../../SaveCardFlow";
import {
    getMultiplierFromPlanName,
    getTierPlanName,
    getTierIndex,
    getMultiplierFromIndex,
    getBasePlanId,
    BUSINESS_TIER_MULTIPLIERS
} from '../../utils/tierUtils';

const { Paragraph } = Typography;
const { Step } = Steps
const stripePromise = loadStripe(ENVIRONMENT.STRIPE_PUBLISH_KEY);
const CUSTOMER_USERNAME = localStorage.getItem('username')


const allowedKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
];

const PlanActivationAndPayment = ({
    selectedPlanName, 
    signUpFlow, 
    cardStatus, 
    updateUserAccountInformation = () => {}, 
    loadUserRequestStatus, 
    closeSubscriptionUpgrade, 
    requestType, 
    selectedBundleQuantity, 
    setSuccessModalVisible = () => {}, 
    setShowPaymentFailureModal = () => {}, 
    setSystemErrorModal = () => {}, 
    annualSetting,
    setAnnualSetting,
    setSubscriptionPaymentProcessing = () => {},
    setPartialCreditsGiven = () => {}
}) => {
    const [plan, setSelectedPlan] = useState(null)
    const [isAnnual, setIsAnnual] = useState(true)
    const [monthlyPrice, setMonthlyPrice] = useState(null)
    const [totalPayment, setTotalPayment] = useState(null)
    const [deleteCardFlow, setDeleteCardFlow] = useState(false)
    const [cardSaveFlowTriggered, setCardSaveFlowTriggered] = useState(false)
    const [stage, setStage] = useState(
        new URLSearchParams(window.location.search).get('setup_intent_client_secret') ? 2 : 1
    )
    const [subscriptionType, setSubscriptionType] = useState('subscription')
    const [bundleQuantity, setBundleQuantity] = useState(1);
    const [refreshPaymentTrigger, setRefreshPaymentTrigger] = useState(0);
    const [selectedTierMultiplier, setSelectedTierMultiplier] = useState(1);
    const [minQuantity,] = useState({
        "Basic Bundle": 5,
        "Studio Bundle": 45,
        "ALL3D Enterprise": 1,
        "Credit Bundle": 5,
    });

    const deleteCardFlowRef = useRef(deleteCardFlow)

    useEffect(() => {
        if(selectedPlanName){
            let selected = null;
            const strippedName = selectedPlanName.replace('annual_', '');
            const basePlanId = getBasePlanId(strippedName);
            const pricingPlan = pricingData.plans.find(p => p.id === basePlanId);

            const multiplier = getMultiplierFromPlanName(strippedName);
            if (multiplier !== null) {
                setSelectedTierMultiplier(multiplier);
            }

            if(pricingPlan && pricingPlan.price !== null){
                const discount = pricingData.discountPercentage;

                let effectivePrice = pricingPlan.price;
                let effectiveCredits = pricingPlan.credits;
                let effectiveFeatures = pricingPlan.features;
                if (multiplier !== null && pricingPlan.tiers) {
                    const tierData = pricingPlan.tiers.find(t => t.multiplier === multiplier);
                    if (tierData) {
                        effectivePrice = tierData.price;
                        effectiveCredits = tierData.credits;
                        effectiveFeatures = tierData.features || pricingPlan.features;
                    }
                }

                const tierPlanName = multiplier !== null ? getTierPlanName(multiplier) : basePlanId;
                const annualMonthly = Math.round(effectivePrice * (1 - discount / 100));
                const annualTotal = Math.round(effectivePrice * (1 - discount / 100) * 12);
                selected = {
                    title: pricingPlan.name,
                    plan_name: tierPlanName,
                    annual_plan_name: `annual_${tierPlanName}`,
                    description: `${effectiveCredits.toLocaleString()} credits per month`,
                    annual_description: `${(effectiveCredits * 12).toLocaleString()} credits annually`,
                    price: annualMonthly,
                    annualTotal: annualTotal,
                    priceMonthly: effectivePrice,
                    features: effectiveFeatures,
                    monthly_features: effectiveFeatures,
                    plus_features: pricingData.advancedFeaturesTable.categories.flatMap(cat =>
                        Object.entries(cat.features)
                            .filter(([, vals]) => !!vals[basePlanId])
                            .map(([name]) => name)
                    ),
                    credits_details: `Your subscription includes ${effectiveCredits.toLocaleString()} credits each month. Unused credits roll over up to 3 months before expiring.`,
                    annual_credits_details: `Your subscription includes all ${(effectiveCredits * 12).toLocaleString()} credits upfront. Unused credits expire 1 year from the subscription start date.`,
                    images_dec: `${effectiveCredits.toLocaleString()} credits monthly`,
                    annual_images_dec: `${(effectiveCredits * 12).toLocaleString()} credits annually`,
                    package_type: pricingPlan.name,
                    hasTiers: !!pricingPlan.tiers,
                    tiers: pricingPlan.tiers,
                };
                setSubscriptionType('subscription');
            }
            else if (selectedPlanName === 'credit_bundle_ai') {
                const cb = pricingData.creditBundle;
                const creditQty = selectedBundleQuantity || cb.minCredits;
                const imageQty = creditQty / cb.creditsPerImage;
                selected = {
                    title: cb.name,
                    name: cb.id,
                    plan_name: cb.id,
                    price: cb.pricePerImage,
                    credit_multiplier: cb.creditsPerImage,
                    features: cb.features,
                    plus_features: [],
                    credits_details: `${cb.creditsPerImage} credits per image. All credits available at once.`,
                };
                setSubscriptionType('bundle');
                setIsAnnual(false);
                setAnnualSetting(false);
                setBundleQuantity(imageQty);
                setTotalPayment(imageQty * cb.pricePerImage);
            }
            else {
                // Try bundles
                selected = bundles.filter(item => item.name == selectedPlanName)[0]
                if(selected){
                    setSubscriptionType('bundle')
                    setIsAnnual(false)
                    setAnnualSetting(false)
                    if(selectedBundleQuantity){
                        setBundleQuantity(selectedBundleQuantity)
                        setTotalPayment(selected.price * selectedBundleQuantity)
                    }
                    else
                        setTotalPayment(selected.price * bundleQuantity)
                }
            }

            setSelectedPlan(selected)
        }
    }, [selectedPlanName])

    useEffect(() => {
        if (plan && subscriptionType == 'subscription') {
            setIsAnnual(annualSetting);
        }
    }, [plan, annualSetting])

    useEffect(() => {
        let monthlyAmount = null
        if (plan && subscriptionType == 'subscription') {
            if (annualSetting == false || isAnnual == false) {
                monthlyAmount = plan.priceMonthly
            }
            else {
                monthlyAmount = plan.price
            }

            setMonthlyPrice(monthlyAmount)
            if (isAnnual || annualSetting) {
                setTotalPayment(plan.annualTotal ?? monthlyAmount * 12)
            }
            else {
                setTotalPayment(monthlyAmount)
            }
        }
        else if(plan && subscriptionType == 'bundle'){
            setMonthlyPrice(plan.price)
            setTotalPayment(plan.price * bundleQuantity)
        }
    }, [plan, isAnnual, subscriptionType, bundleQuantity])

    // Update the ref whenever count changes
  useEffect(() => {
    deleteCardFlowRef.current = deleteCardFlow;
  }, [deleteCardFlow]);

    useEffect(() => {
        if (plan && plan.hasTiers && selectedTierMultiplier) {
            const tierData = plan.tiers.find(t => t.multiplier === selectedTierMultiplier);
            if (tierData) {
                const discount = pricingData.discountPercentage;
                const annualMonthly = Math.round(tierData.price * (1 - discount / 100));
                const annualTotal = Math.round(tierData.price * (1 - discount / 100) * 12);
                const newPlanName = getTierPlanName(selectedTierMultiplier);

                setSelectedPlan(prev => ({
                    ...prev,
                    plan_name: newPlanName,
                    annual_plan_name: `annual_${newPlanName}`,
                    description: `${tierData.credits.toLocaleString()} credits per month`,
                    annual_description: `${(tierData.credits * 12).toLocaleString()} credits annually`,
                    price: annualMonthly,
                    annualTotal: annualTotal,
                    priceMonthly: tierData.price,
                    features: tierData.features || prev.features,
                    monthly_features: tierData.features || prev.monthly_features,
                    credits_details: `Your subscription includes ${tierData.credits.toLocaleString()} credits each month. Unused credits roll over up to 3 months before expiring.`,
                    annual_credits_details: `Your subscription includes all ${(tierData.credits * 12).toLocaleString()} credits upfront. Unused credits expire 1 year from the subscription start date.`,
                    images_dec: `${tierData.credits.toLocaleString()} credits monthly`,
                    annual_images_dec: `${(tierData.credits * 12).toLocaleString()} credits annually`,
                }));

                const URLParams = new URLSearchParams(window.location.search);
                const urlPlan = URLParams.get('plan');
                if (urlPlan) {
                    const wasAnnual = urlPlan.includes('annual_');
                    URLParams.set('plan', wasAnnual ? `annual_${newPlanName}` : newPlanName);
                    window.history.replaceState({}, '', `${window.location.pathname}?${URLParams.toString()}`);
                }
            }
        }
    }, [selectedTierMultiplier]);



    const initiateConfirmation = (deleteFlow = false) => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          
        if ((cardStatus == 1 || signUpFlow == true) && !deleteFlow) {
            setStage(2);
            return
        }

        window.addEventListener('card_saved', (e) => {
            if(deleteCardFlowRef.current){
                // delete Previous Card
                axios.post(ENVIRONMENT.GET_USER_PAYMENT_INFORMATION, {
                    'username': localStorage.getItem('username'),
                    'action': 'delete_previous'
                }).then((res) => {
                    updateUserAccountInformation(res.data)
                    setStage(1)
                    setDeleteCardFlow(false)
                })
            }
            let action = e.detail.action;
            setCardSaveFlowTriggered(false);
            if (action == 'save_card') {
                loadUserRequestStatus();
                setStage(2);
                setRefreshPaymentTrigger(prev => prev + 1);
            }

        }, false);

        if (cardStatus == 0 || deleteFlow) {
            let child_window = window.open('/save-card?action=save_card', '', "width=600,height=600");
            let refreshInterval = setInterval(() => {
                if(child_window.closed) {
                setCardSaveFlowTriggered(false);
                clearInterval(refreshInterval);
                }
            }, 500);
            setCardSaveFlowTriggered(true);
        }
    }

    const changePaymentMethod = () => {
        // Delete the payment method and show form 
        // setDeleteCardFlow(true)
        initiateConfirmation(true)
    }

    const customDot = (dot, { status }) => {
        switch (status) {
          case "process":
            return <div className="custom-dot-process" />;
          case "finish":
            return (
              <div className="custom-dot-finish">
                {" "}
                <CheckOutlined className="check-icon" />{" "}
              </div>
            );
          default:
            return dot;
        }
      };


    return (
        <div style={{ width: '100%' }}>
            {selectedPlanName && selectedPlanName !== 'free_trial_ai' &&
                <div className="steps-tab-register">
                    <Steps progressDot={customDot}
                    labelPlacement="vertical" className='step-class' style={{ marginBottom: 0, padding: '32px 48px' }} size="small" current={stage}>
                        <Step className={`manrope f-14 `} title={<span className="manrope f-14 w-500 lh-22 black-085">Select Subscription</span>} />
                        <Step className={`manrope f-14 `} title={<span className="manrope f-14 w-500 lh-22 black-085">Review Order</span>} />
                        <Step className={`manrope f-14 `} title={<span className="manrope f-14 w-500 lh-22 black-085">Checkout</span>} />
                    </Steps>
                </div>
            }
            {plan &&
                <>
                    <div className="selected-plan-heading-gradient" >
                        <div className="manrope f-30 w-700 black-085 lh-32">
                            {plan?.title}
                        </div>
                        {plan?.description && subscriptionType == 'subscription' &&
                        <div className="manrope f-16 w-500 black-085 lh-24 mt-8">
                            {isAnnual ? plan?.annual_description : plan?.description}
                        </div>}
                    </div>
                    <div className="subscription-card" style={{ padding: '32px 25px', borderRadius: '4px' }}>
                            {stage == 1 ? 
                            subscriptionType == 'subscription' ?
                            <>
                            <div className="price-section" style={{ marginBottom: 8, alignItems: 'baseline', flexWrap: 'nowrap' }}>
                                <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'baseline' }}>
                                    <span className="manrope f-26 w-700 black-085" style={{ lineHeight: '1' }}>${monthlyPrice}</span>
                                    <span className="manrope f-14 w-500 black-085" style={{ lineHeight: '1' }}>/month</span>
                                </span>
                                <span className="manrope f-14 w-400 lh-22 gray-045">{isAnnual ? 'Billed annually' : 'Billed monthly'}</span>
                            </div>
                            <div className="manrope f-14 w-400 lh-22 black-085" style={{ marginBottom: 24 }}>
                                {isAnnual ? plan.annual_images_dec : plan.images_dec}
                            </div>
                            <div className="gray-billing-bg">
                            <div className="justify-space-between" style={{ marginBottom: 8 }}>
                                <span className="manrope f-14 w-600 lh-22 black-085">Your billing cycle</span>
                                <span className="manrope f-14 w-600 lh-22 black-085">
                                ${isAnnual ? (plan.annualTotal ?? parseInt(monthlyPrice) * 12).toLocaleString() : monthlyPrice.toLocaleString()} billed {isAnnual ? 'annually' : 'monthly'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span className={`manrope f-14 lh-22 ${!isAnnual ? 'w-600' : 'w-500'} black-085`}>Monthly</span>
                                    <Switch
                                        className="blue-color"
                                        style={{ marginLeft: 8, marginRight: 8 }}
                                        checked={isAnnual}
                                        onChange={(checked) => {
                                            setIsAnnual(checked)
                                            setAnnualSetting(checked)
                                            let URLParams = new URLSearchParams(window.location.search);
                                            let plan = URLParams.get('plan')
                                            
                                            if (plan && plan.includes('annual_') && !checked) {
                                                URLParams.set('plan', plan.replace('annual_', ''))
                                            }
                                            else if (plan && !plan.includes('annual_') && checked) {
                                                URLParams.set('plan', 'annual_' + plan)
                                            }
                                            window.history.replaceState({}, '', `${window.location.pathname}?${URLParams.toString()}`);
                                        }}
                                    />
                                    <span className={`manrope f-14 lh-22 ${isAnnual ? 'w-600' : 'w-500'} black-085`}>Annually</span>
                                </div>
                                <div className={`save-discount-bg ${isAnnual ? 'blue' : 'gray'} ml-12`}>
                                    <span className={`manrope f-12 lh-20 ${isAnnual ? 'w-700 blue-18' : 'w-500 black-085'}`}>
                                        Save {pricingData.discountPercentage}%
                                    </span>
                                </div>
                            </div>
                            </div>

                            {plan.hasTiers && (
                                <div style={{ marginTop: 24, marginBottom: 24 }}>
                                    <div className="manrope f-14 w-600 lh-22 black-085" style={{ marginBottom: 8 }}>
                                        Scale your plan
                                    </div>
                                    <div className="slider-container" style={{ paddingBottom: 5 }}>
                                        <Slider
                                            min={0}
                                            max={BUSINESS_TIER_MULTIPLIERS.length - 1}
                                            step={1}
                                            dots
                                            value={getTierIndex(selectedTierMultiplier)}
                                            onChange={(index) => setSelectedTierMultiplier(getMultiplierFromIndex(index))}
                                            tooltipVisible={false}
                                            trackStyle={{ backgroundColor: '#276dd7' }}
                                            handleStyle={{ borderColor: '#276dd7', backgroundColor: '#276dd7' }}
                                            marks={BUSINESS_TIER_MULTIPLIERS.reduce((marks, mult, idx) => {
                                                marks[idx] = `${mult}x`;
                                                return marks;
                                            }, {})}
                                        />
                                    </div>
                                </div>
                            )}

                            {plan.credits_details && !signUpFlow &&
                            <div className="credit-system-bg" style={{ marginTop: 24, marginBottom: 24 }}>
                                <div className="manrope f-14 w-700 lh-22 black-085 text-left" style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <img src="/img/majesticons_coins-line.svg" className="mr-8" style={{ width: 20, height: 20 }}/>
                                    Your subscription runs on credits
                                </div>
                                <div className="manrope f-14 w-500 lh-22 black-085 text-left">
                                    {isAnnual ? plan.annual_credits_details : plan.credits_details}
                                </div>
                            </div>}

                            {/* <div className="subscription-details">
                                Your subscription includes full access to the ALL3D platform and
                                its features including any combination of 3D/AR models, images,
                                and 360 spins within your paid quota, free access to the ALL3D
                                store, cloud storage, and sharing.
                            </div> */}

                            <div style={{ display: 'flex', gap: '12px', marginTop: 24 }}>
                                <Button className="back-btn" style={{ flex: '0 0 25%' }} onClick={closeSubscriptionUpgrade}>Back</Button>
                                <Button className="checkout-btn" style={{ flex: '0 0 calc(75% - 12px)' }} onClick={() => initiateConfirmation()}>{(cardStatus == 1 || signUpFlow) ? 'Continue to checkout': 'Add Payment Method'}</Button>
                            </div>

                            <div className="subscription-note" style={{ marginTop: 24, textAlign: 'center' }}>
                                {isAnnual ? (
                                    <span>
                                        This is an annual contract which will automatically renew each year. You may cancel at any time before the renewal date.
                                    </span>
                                ) : (
                                    <span>
                                        This is a monthly contract which will automatically renew each month. You may cancel at any time before the renewal date.
                                    </span>
                                )}
                            </div>
                            </> 
                            :
                            // Selecting a Bundle Flow
                            <>
                            <div className="price-section" style={{ marginBottom: 16 }}>
                                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                <span className="manrope f-26 w-700 lh-40 black-085">${selectedPlanName === 'credit_bundle_ai' ? monthlyPrice?.toFixed(2) : monthlyPrice}</span>
                                <span className="manrope f-14 w-600 lh-40 black-085">{selectedPlanName === 'credit_bundle_ai' ? '/image' : '/product'}</span>
                                </div>
                                <Paragraph className="manrope black-085 f-14 lh-28 w-400 gray-045" style={{marginBottom: 0}}>One-time purchase</Paragraph>
                            </div>
                            <div className="justify-space-between">
                                <div className="justify-in-start">
                                    <div className="manrope f-14 lh-22 black-085 w-600 mr-8">{selectedPlanName === 'credit_bundle_ai' ? 'IMAGES' : 'QTY'}</div>
                                        <InputNumber style={{ height: 33 }}
                                        min={minQuantity[plan.title] || 1}
                                            onKeyDown={(e) => {
                                                if ((["-", ".", " "].includes(e.key)) || (/[a-zA-Z\s]/.test(e.key) && !allowedKeys.includes(e.key))) {
                                                    e.preventDefault();
                                                }
                                            }}

                                            value={bundleQuantity}
                                            onChange={(value) => setBundleQuantity(value)}
                                            className="manrope f-16 lh-24 black-085 w-400" />
                                    </div>
                                    {selectedPlanName === 'credit_bundle_ai' &&
                                        <div className="manrope f-14 lh-22 black-085 w-400" style={{ opacity: 0.6 }}>
                                            {bundleQuantity} images &bull; {bundleQuantity * plan.credit_multiplier} credits
                                        </div>
                                    }
                                    <div className="justify-in-end total-payment-bg">
                                        <div className="manrope f-14 lh-22 black-085 w-700 mr-20">
                                            Total
                                        </div>
                                        <div className="manrope f-14 lh-22 black-085 w-500">
                                            ${selectedPlanName === 'credit_bundle_ai' ? totalPayment?.toFixed(2) : totalPayment?.toLocaleString()}
                                        </div>
                                    </div>
                            </div>


                            <Button className="checkout-btn" style={{ marginTop: 20 }} onClick={() => initiateConfirmation()}>{(cardStatus == 1 || signUpFlow) ? 'Continue to Checkout': 'Add Payment Method'}</Button>
                            <div className="subscription-note">
                                <span>
                                Your total price for this bundle is ${selectedPlanName === 'credit_bundle_ai' ? totalPayment?.toFixed(2) : totalPayment}. This is a one-time purchase. You may purchase more bundles or upgrade to a subscription at any time.
                                </span>
                            </div>
                            </>
                            :
                             <PaymentConfirmation
                                totalAmount={totalPayment}
                                plan={plan.plan_name || selectedPlanName}
                                annualPlan={isAnnual}
                                setStage={setStage}
                                signUpFlow={signUpFlow}
                                cardStatus={cardStatus}
                                closeSubscriptionUpgrade={closeSubscriptionUpgrade}
                                loadUserRequestStatus={loadUserRequestStatus}
                                requestType={requestType}
                                bundleQuantity={bundleQuantity}
                                subscriptionType={subscriptionType}
                                changePaymentMethod={changePaymentMethod}
                                setSuccessModalVisible={setSuccessModalVisible}
                                setShowPaymentFailureModal={setShowPaymentFailureModal}
                                setSystemErrorModal={setSystemErrorModal}
                                setSubscriptionPaymentProcessing={setSubscriptionPaymentProcessing}
                                setPartialCreditsGiven={setPartialCreditsGiven}
                                refreshPaymentTrigger={refreshPaymentTrigger}
                            />
                            }
                        </div>
                    </>
                }
            {signUpFlow && 
            <div className='text-center manrope f-12 w-400 grey-66 mt-40 mb-40' style={{ padding: '0 48px' }}>By continuing, you accept ALL3D's <a href="https://all3d.ai/terms">Terms of Use</a> and <a href="https://all3d.ai/privacy">Privacy Policy</a>.</div>
            }
        </div>
    );
};

const PaymentConfirmation = ({ totalAmount, plan, annualPlan, setStage, signUpFlow, cardStatus, closeSubscriptionUpgrade, loadUserRequestStatus, requestType, bundleQuantity, subscriptionType, changePaymentMethod, setSuccessModalVisible, setShowPaymentFailureModal, setSystemErrorModal, setSubscriptionPaymentProcessing, setPartialCreditsGiven, refreshPaymentTrigger}) => {
    const [paymentLoader, setPaymentLoader] = useState(false)
    const [paymentInformation, setPaymentInformation] = useState(null)
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

    useEffect(() => {

        let payload = {
            "username": localStorage.getItem('username'),
            "action": "getinfo"
          }
          axios.post(ENVIRONMENT.GET_USER_PAYMENT_INFORMATION, payload)
          .then(res => {
            setPaymentInformation(res.data['card_information'])
            if (res.data['card_information'] && res.data['card_information'].length > 0) {
                // If previously selected method exists in new list, keep it, otherwise select first
                if (selectedPaymentMethod && res.data['card_information'].some(c => c.id === selectedPaymentMethod)) {
                    // keep current selection
                } else {
                    setSelectedPaymentMethod(res.data['card_information'][0].id)
                }
            }
          });

    }, [refreshPaymentTrigger])

    const handlePaymentAndSubscriptionActivation = () => {
        setPaymentLoader(true)
        let payload = {
            subscription_type: annualPlan ? "annual_" + plan : plan,
            username: localStorage.getItem('username'),
            annual_subscription: annualPlan,
            payment_method_stripe_id: selectedPaymentMethod
        }
        if(subscriptionType == 'bundle' && bundleQuantity){
            payload['bundle_quantity'] = bundleQuantity
        }
        // Subscription Activation
        axios.post(ENVIRONMENT.UPGRADE_SUBSCRIPTION, payload).then(res => {
            setPaymentLoader(false)
           
            closeSubscriptionUpgrade()
            if(res.data.status_code == 400){
                if(res.data.error_type == 'payment_failed')
                    setShowPaymentFailureModal(true)
                else
                    setSystemErrorModal(true)
            }
            else{
                loadUserRequestStatus();
                if(res.data.payment_status == 'processing'){
                    setSubscriptionPaymentProcessing(true)
                }
                if(res.data.credits_pct_given){
                    setPartialCreditsGiven(res.data.credits_pct_given * 100)
                }
                setSuccessModalVisible(true)
            }
        })
    }

    return (
        <span>
            {
                signUpFlow ?
                <SaveCardFlow
                totalAmount={totalAmount}
                account_details_form={false}
                signUpFlow={signUpFlow}
                plan={plan}
                isAnnual={annualPlan}
                subscriptionType={subscriptionType}
                bundleQuantity={bundleQuantity}
                setCardSaveFailure={() => alert('Card save failed')}/>
                :
                <span>
                    { (cardStatus == 1 && paymentInformation != null) ? 
                    <div>
                         <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                             <div className="manrope f-16 w-600 black-085">Select Payment Method</div>
                             <a onClick={changePaymentMethod} className="manrope f-16 w-400 lh-24 blue" style={{ cursor: 'pointer' }}>
                                Add Payment Method
                            </a>
                         </div>
                        <div style={{ marginBottom: 24 }}>
                            <Radio.Group onChange={(e) => setSelectedPaymentMethod(e.target.value)} value={selectedPaymentMethod} style={{ width: '100%' }}>
                                {paymentInformation.map((info) => (
                                    <div key={info.id} className="payment-method-row" style={{ display: 'flex', alignItems: 'center', marginBottom: 16, border: '1px solid #d9d9d9', padding: '12px', borderRadius: '4px' }}>
                                        <Radio value={info.id} style={{ marginRight: 16 }} />
                                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            {info.type === 'us_bank_account' ? (
                                                <>
                                                    <BankOutlined style={{ fontSize: '20px', color: '#1890ff', marginRight: '24px' }} />
                                                    <span className="manrope f-16 w-600 lh-24 black-085">
                                                        {info.us_bank_account.bank_name}
                                                    </span>
                                                    <span className="manrope f-16 w-600 lh-24 black-085" style={{ marginLeft: 'auto' }}>
                                                        ****{info.us_bank_account.last4}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <CreditCardOutlined style={{ fontSize: '20px', color: '#1890ff', marginRight: '24px' }} />
                                                    <span className="manrope f-16 w-600 lh-24 black-085" style={{ marginRight: '24px' }}>
                                                        {info.card.brand.charAt(0).toUpperCase() + info.card.brand.slice(1)}
                                                    </span>
                                                    <span className="manrope f-16 w-600 lh-24 black-085">
                                                        ****{info.card.last4}
                                                    </span>
                                                    <span className="manrope f-16 w-400 lh-24 black-085" style={{ marginLeft: 'auto' }}>
                                                        Expires {String(info.card.exp_month).padStart(2, "0")}/{info.card.exp_year}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </Radio.Group>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '12px', marginTop: 24 }}>
                            <Button onClick={() => setStage(1)} className="back-btn" style={{ flex: '0 0 25%' }}>
                                Back
                            </Button>
                            <Button 
                                onClick={() => handlePaymentAndSubscriptionActivation()} 
                                disabled={paymentLoader} 
                                className="checkout-btn" 
                                style={{ flex: '0 0 calc(75% - 12px)' }}>
                                {paymentLoader ? <LoadingOutlined className="manrope f-14" /> : ''} Pay ${totalAmount?.toLocaleString()}
                            </Button>
                        </div>

                        <div className="subscription-note" style={{ marginTop: 24, textAlign: 'center' }}>
                            {subscriptionType === 'bundle' ? (
                                <span>
                                    This is a one-time purchase. You may purchase more bundles or upgrade to a subscription at any time.
                                </span>
                            ) : annualPlan ? (
                                <span>
                                    This is an annual contract which will automatically renew each year. You may cancel at any time before the renewal date.
                                </span>
                            ) : (
                                <span>
                                    This is a monthly contract which will automatically renew each month. You may cancel at any time before the renewal date.
                                </span>
                            )}
                        </div>
                    </div>:
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                        <LoadingOutlined className="manrope f-16" />
                    </div>
                    }
                </span>
                
            }
        </span>
    );


}

export default PlanActivationAndPayment;
